import { useEffect, useRef } from "react";
import { Button, Text, VStack, Badge, Box, HStack } from "@chakra-ui/react";
import toast from "react-hot-toast";
import {
  useGetProfileQuery,
  useCreateCheckoutSessionMutation,
  useConfirmCheckoutSessionMutation,
} from "../../../../redux/reducers/apiSlice";

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="8" fill="#22c55e" />
    <path d="M4.5 8l2.5 2.5 4.5-4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PAID_FEATURES = [
  "Unlimited Sparks",
  "Full metrics and programs access",
  "Upload & share up to 5 documents (Strategic Plan, 990, Annual Report, etc.)",
  "Golden ring on your profile picture",
  "Priority support",
];

const TabSubscription = () => {
  // Always refetch fresh from server when this tab is opened — never use stale cache
  const { data: profile, refetch: refetchProfile, isFetching } = useGetProfileQuery(undefined, { refetchOnMountOrArgChange: true });
  const [createCheckoutSession, { isLoading }] = useCreateCheckoutSessionMutation();
  const [confirmCheckoutSession, { isLoading: isConfirming }] = useConfirmCheckoutSessionMutation();
  const autoConfirmRan = useRef(false);

  // On mount: if there's a pending session (from recent Stripe redirect), auto-confirm
  useEffect(() => {
    if (autoConfirmRan.current) return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id") || sessionStorage.getItem("pending_session_id");
    if (!sessionId) {
      // No pending session, still make sure we have freshest profile
      refetchProfile();
      return;
    }
    autoConfirmRan.current = true;
    const run = async () => {
      try {
        await confirmCheckoutSession({ session_id: sessionId }).unwrap();
        sessionStorage.removeItem("pending_session_id");
        // invalidatesTags: ["User"] on the mutation will auto-refetch the profile,
        // but we also force a refetch to avoid any cache race conditions.
        await refetchProfile();
      } catch {
        // Session may already be confirmed — just refetch profile to sync
        await refetchProfile();
      }
    };
    run();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Manual confirm: if user already paid but status didn't update, try confirming again
  const handleManualConfirm = async () => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id") || sessionStorage.getItem("pending_session_id");
    if (sessionId) {
      try {
        await confirmCheckoutSession({ session_id: sessionId }).unwrap();
        sessionStorage.removeItem("pending_session_id");
      } catch (e) {
        // ignore
      }
    }
    await refetchProfile();
    toast.success("Status refreshed!");
  };

  const isPaid = profile?.subscription_tier === "PAID";
  const hasPendingSession = !!(
    (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("session_id")) ||
    sessionStorage.getItem("pending_session_id")
  );
  // Show loading while we're auto-confirming a recent payment
  const isAutoConfirming = isConfirming || (hasPendingSession && isFetching && !isPaid);

  const handleUpgrade = async () => {
    try {
      const origin = window.location.origin;
      const result = await createCheckoutSession({
        successUrl: `${origin}/?upgrade=success&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${origin}/?upgrade=cancelled`,
      }).unwrap();

      if (result?.url) {
        window.location.href = result.url;
      } else {
        toast.error("Could not start checkout. Please try again.");
      }
    } catch (err: any) {
      console.error("CheckoutError", err);
      toast.error(err?.data?.message || "Something went wrong. Please try again.");
    }
  };

  return (
    <VStack align="start" spacing={5} p={2}>
      {isAutoConfirming ? (
        /* ── CONFIRMING PAYMENT ──────────────────────────────── */
        <Box w="100%" p={6} borderRadius="xl" bg="blue.50" border="2px solid" borderColor="blue.200" textAlign="center">
          <Text fontSize="sm" color="blue.700" fontWeight="medium" mb={1}>
            ⏳ Confirming your payment…
          </Text>
          <Text fontSize="xs" color="blue.500">
            Please wait, this only takes a moment.
          </Text>
        </Box>
      ) : isPaid ? (
        /* ── PAID STATE ─────────────────────────────────────────── */
        <Box w="100%" p={5} borderRadius="xl" bg="green.50" border="2px solid" borderColor="green.300">
          <HStack mb={3}>
            <Badge colorScheme="green" fontSize="sm" px={3} py={1} borderRadius="full">
              ✓ Active – Paid Plan ($19/month)
            </Badge>
          </HStack>
          <Text fontSize="sm" color="gray.700" mb={4}>
            Thank you for supporting IGNITE! You have full access to all premium features:
          </Text>
          <VStack align="start" spacing={2} mb={4}>
            {PAID_FEATURES.map((f) => (
              <HStack key={f} spacing={2}>
                <CheckIcon />
                <Text fontSize="sm" color="gray.700">{f}</Text>
              </HStack>
            ))}
          </VStack>
          <Box p={3} bg="green.100" borderRadius="lg">
            <Text fontSize="xs" color="green.800" fontWeight="medium">
              📄 Go to the <strong>Documents</strong> tab to upload your Strategic Plan, 990, Annual Report, and other files.
            </Text>
            <Text fontSize="xs" color="green.700" mt={1}>
              Recurring billing is managed through Stripe. To cancel, contact support.
            </Text>
          </Box>
        </Box>
      ) : (
        /* ── FREE STATE ─────────────────────────────────────────── */
        <Box w="100%">
          <Box p={5} borderRadius="xl" bg="orange.50" border="2px solid" borderColor="orange.200" mb={4}>
            <Badge colorScheme="orange" fontSize="sm" px={3} py={1} borderRadius="full" mb={3}>
              Free Plan
            </Badge>
            <Text fontSize="sm" color="gray.700" mb={4}>
              Upgrade to the <strong>paid plan for $19/month</strong> to unlock:
            </Text>
            <VStack align="start" spacing={2} mb={4}>
              {PAID_FEATURES.map((f) => (
                <HStack key={f} spacing={2}>
                  <Text color="orange.400" fontSize="sm">→</Text>
                  <Text fontSize="sm" color="gray.700">{f}</Text>
                </HStack>
              ))}
            </VStack>
            <Button
              colorScheme="orange"
              size="md"
              w="auto"
              alignSelf="flex-start"
              onClick={handleUpgrade}
              isLoading={isLoading}
              loadingText="Redirecting to Stripe…"
              borderRadius="full"
            >
              Upgrade — $19/month
            </Button>
            <Text fontSize="xs" color="gray.500" mt={2} textAlign="center">
              Secure payment via Stripe. Cancel anytime.
            </Text>
          </Box>
          <Box mt={2} textAlign="center">
            <Button
              variant="ghost"
              size="xs"
              colorScheme="gray"
              onClick={handleManualConfirm}
              isLoading={isFetching || isConfirming}
              loadingText="Checking…"
            >
              Already paid? Click here to refresh your status
            </Button>
          </Box>
        </Box>
      )}
    </VStack>
  );
};

export default TabSubscription;
